import { useCallback, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { PatientFormData } from "components/views/patientDetails/patientEdit";
import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { extractValidationErrors } from "lib/extrahorizon/utils";

import { convertToExhPatientData } from "../utils/patientConverters";

export type UpdatePatientResult =
  | { status: "success" }
  | { status: "validation_error"; errors: Record<string, string> };

interface UseUpdateExhPatientResult {
  updatePatient: (patientId: string, formData: PatientFormData) => Promise<UpdatePatientResult | null>;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for updating patients via ExtraHorizon client
 * Uses the patient schema which is intercepted and redirected to patient-endpoint
 */
export const useUpdateExhPatient = (): UseUpdateExhPatientResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { showBoundary: onError } = useErrorBoundary();

  const updatePatient = useCallback(
    async (patientId: string, formData: PatientFormData): Promise<UpdatePatientResult | null> => {
      if (!isExhClientInitialized()) {
        const err = new Error("ExtraHorizon client not initialized");
        setError(err);
        onError(err);
        return null;
      }

      try {
        setLoading(true);
        setError(null);

        const client = getExhClient();
        const exhData = convertToExhPatientData(formData);

        await client.data.documents.update("patient", patientId, exhData);

        return { status: "success" };
      } catch (err) {
        const validationErrors = extractValidationErrors(err);
        if (validationErrors) {
          return { status: "validation_error", errors: validationErrors };
        }
        const error = err instanceof Error ? err : new Error("Failed to update patient");
        setError(error);
        onError(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [onError]
  );

  return { updatePatient, loading, error };
};
